import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Always call /auth/me: the backend recognises either the JWT bearer token
    // (email/password flow) or the httpOnly session_token cookie (Google flow).
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("pc_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("pc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    // Exchange the Emergent OAuth session_id for an app session cookie.
    // Server sets an httpOnly `session_token` cookie automatically — we just
    // reflect the returned user object into React state and route accordingly.
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    setUser(data.user);
    return { user: data.user, needs_onboarding: data.needs_onboarding };
  };

  const logout = async () => {
    // Best-effort clear on the server (kills session cookie + row).
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("pc_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, loginWithGoogleSession, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
